import {ButtonProps, closeDialog, openDialog} from 'platform/components';
import {Box, HStack} from 'platform/foundation';

import {equals, isNil, not} from 'ramda';
import {isNilOrEmpty} from 'ramda-adjunct';

import {
  PostWorkBasketItemFromLabourCatalogApiArg,
  useDeleteWorkBasketItemsMutation,
  useGetServiceOrderJobQuery,
  useGetServiceOrderQuery,
  useGetServiceOrderVariantQuery,
  useGetWorkBasketItemsQuery,
  usePatchOrderItemForWorkMutation,
  usePatchWorkBasketItemMutation,
  usePostServiceItemsLabourSettingsMutation,
  usePostWorkBasketItemFromLabourCatalogMutation,
  usePostWorkBasketItemsToOrderMutation,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {
  EitherQuantityOrError,
  FullScreenModal,
  handleApiError,
  queryParams,
  useInvalidBasketItemsIds,
} from '@dms/shared';
import {
  AfterSalesLabourBasket,
  LabourBasketItemEditingDetails,
  useBasketMechanic,
} from '@dms/shared-aftersales';

import {buildArray, RequiredTestIdProps, suffixTestId, useQueryState} from 'shared/web';

import {BulkEditItem} from '../../../../../components/BulkEditItem/BulkEditItem';
import {EditItem} from '../../../../../components/EditItem/EditItem';
import {useWorkshopUrl} from '../../../../../hooks/useWorkshopUrl';
import {LabourBasketItemType} from '../../../../../types/LabourBasketItemType';
import {ItemListCard} from './ItemListCard';

interface AddLabourModalProps extends RequiredTestIdProps {
  onSubmit: VoidFunction;
}

export function AddLabourModal(props: AddLabourModalProps) {
  const {serviceCaseId, orderId, addLabourJobId} = useWorkshopUrl();
  const [, , closeModal] = useQueryState(queryParams.SERVICE_CASE_ADD_LABOUR_JOB_ID);

  const {data: serviceJob} = useGetServiceOrderJobQuery({
    serviceCaseId,
    serviceOrderId: orderId,
    serviceJobId: addLabourJobId,
  });
  const {data: serviceOrder} = useGetServiceOrderQuery({serviceCaseId, serviceOrderId: orderId});

  const {data: orderVariant} = useGetServiceOrderVariantQuery(
    {serviceOrderVariantId: serviceOrder?.serviceOrderVariantId ?? ''},
    {skip: isNil(serviceOrder?.serviceOrderVariantId)}
  );

  const {
    data: basket,
    isLoading: isBasketLoading,
    isFetching: isRefreshingBasket,
    isError: hasBasketError,
  } = useGetWorkBasketItemsQuery({
    serviceCaseId,
    serviceOrderId: orderId,
    serviceJobId: addLabourJobId,
  });

  const [postWorkBasketItemFromLabourCatalog, {isLoading: isAddingItemsToBasket}] =
    usePostWorkBasketItemFromLabourCatalogMutation();

  const [deleteWorkBasketItems, {isLoading: isDeletingBasketItems}] =
    useDeleteWorkBasketItemsMutation();

  const [postWorkBasketItemsToOrder, {isLoading: isCheckingOutBasket}] =
    usePostWorkBasketItemsToOrderMutation();

  const [patchWorkBasketItem, {isLoading: isChangingQuantity}] = usePatchWorkBasketItemMutation();

  const [, {isLoading: isEditingLabourItem}] = usePatchOrderItemForWorkMutation({
    fixedCacheKey: 'edit-service-order-work-item',
  });

  const [, {isLoading: isBulkEditingLabourItems}] = usePostServiceItemsLabourSettingsMutation({
    fixedCacheKey: 'bulk-edit-service-order-work-item',
  });

  const basketItems = basket?.workBasketItem ?? [];

  const isBasketEmpty = isNilOrEmpty(basketItems);

  const {setInvalidBasketItemId, hasInvalidBasketItems} = useInvalidBasketItemsIds(basketItems);

  const hasPendingRequest =
    isRefreshingBasket ||
    isAddingItemsToBasket ||
    isCheckingOutBasket ||
    isDeletingBasketItems ||
    isChangingQuantity ||
    isEditingLabourItem ||
    isBulkEditingLabourItems;

  const {mechanics, areMechanicsLoading, mechanicPayload, inputMechanicId, handleMechanicChange} =
    useBasketMechanic({
      authorizationProfileId: orderVariant?.authorizationProfileId,
      assignedMechanicId: serviceJob?.assignMechanics?.[0].id,
    });

  const handleCancel = () => {
    deleteWorkBasketItems({
      serviceCaseId,
      serviceOrderId: orderId,
      serviceJobId: addLabourJobId,
      body: {serviceItemId: basketItems?.map((item) => item.id) ?? []},
    })
      .unwrap()
      .catch(handleApiError)
      .finally(closeModal);
  };

  const handleCheckout = () => {
    postWorkBasketItemsToOrder({
      serviceCaseId,
      serviceOrderId: orderId,
      serviceJobId: addLabourJobId,
      body: mechanicPayload
        ? {
            assignMechanics: [mechanicPayload],
          }
        : undefined,
    })
      .unwrap()
      .then(() => {
        closeModal();
        props.onSubmit();
      })
      .catch(handleApiError);
  };

  const handleAddLabourItem = async (arg: PostWorkBasketItemFromLabourCatalogApiArg) =>
    await postWorkBasketItemFromLabourCatalog(arg).unwrap().catch(handleApiError);

  const handleQuantityChange = async (itemId: string, quantity: EitherQuantityOrError) => {
    setInvalidBasketItemId(itemId, quantity);

    if (isNil(quantity.newQuantity) || quantity.hasError) {
      return;
    }

    const basketItem = basketItems?.find((item) => equals(item.id, itemId));
    const hasQuantityChanged = not(equals(basketItem?.quantity, quantity.newQuantity));

    if (not(hasQuantityChanged)) {
      return;
    }

    await patchWorkBasketItem({
      serviceCaseId,
      serviceOrderId: orderId,
      serviceJobId: addLabourJobId,
      serviceItemId: itemId,
      body: {
        quantity: quantity.newQuantity,
      },
    })
      .unwrap()
      .catch(handleApiError);
  };

  const handleEditLabourItem = ({
    itemId,
    itemName,
    priceTypeName,
  }: LabourBasketItemEditingDetails) => {
    if (isNil(itemId)) {
      return;
    }

    openDialog(
      <EditItem
        itemId={itemId}
        onClose={() => closeDialog('editBasketItem')}
        serviceCaseId={serviceCaseId}
        serviceOrderId={orderId}
        serviceJobId={addLabourJobId}
        data-testid={suffixTestId('editItem', props)}
      />,
      {
        title: `${itemName ?? ''} ${priceTypeName ? `(${priceTypeName})` : ''}`,
        id: 'editBasketItem',
        withAdditionalFooter: true,
      }
    );
  };

  const handleDeleteLabourItem = async (itemId: string) => {
    await deleteWorkBasketItems({
      serviceCaseId,
      serviceOrderId: orderId,
      serviceJobId: addLabourJobId,
      body: {serviceItemId: [itemId]},
    })
      .unwrap()
      .catch(handleApiError);
  };

  const removeItems = (items: LabourBasketItemType[]) =>
    deleteWorkBasketItems({
      serviceCaseId,
      serviceOrderId: orderId,
      serviceJobId: addLabourJobId,
      body: {serviceItemId: items.map((item) => item.id)},
    })
      .unwrap()
      .catch(handleApiError);

  const openItemsSettings = (items: LabourBasketItemType[]) => {
    openDialog(
      <BulkEditItem
        items={items}
        onClose={() => closeDialog('bulkEditBasketItem')}
        serviceCaseId={serviceCaseId}
        serviceOrderId={orderId}
        serviceJobId={addLabourJobId}
        data-testid={suffixTestId('editItem', props)}
      />,
      {
        title: i18n.t('entity.addWork.actions.itemsSettings'),
        id: 'bulkEditBasketItem',
      }
    );
  };

  const actions = buildArray<ButtonProps>()
    .add({
      isLoading: hasPendingRequest,
      title: i18n.t('general.actions.discard'),
      variant: 'secondary',
      onClick: handleCancel,
      'data-testid': suffixTestId('cancel', props),
    })
    .add({
      isLoading: hasPendingRequest,
      isDisabled: isBasketEmpty || hasInvalidBasketItems,
      title: i18n.t('general.actions.add'),
      onClick: handleCheckout,
      'data-testid': suffixTestId('add', props),
    });

  return (
    <FullScreenModal headline={i18n.t('entity.addWork.actions.addWork')} actions={actions}>
      <Box padding={4} height="100%">
        <HStack spacing={4} height="100%">
          <Box flex={5}>
            <ItemListCard
              onAddToBasket={handleAddLabourItem}
              data-testid={suffixTestId('itemList', props)}
            />
          </Box>
          <Box flex={1} minWidth={86}>
            <AfterSalesLabourBasket<LabourBasketItemType>
              basket={{
                items: basketItems,
                totalPrice: basket?.workBasketTotalPrice,
                isLoading: isBasketLoading,
                hasError: hasBasketError,
                hasPendingRequest,
                hasInvalidItems: hasInvalidBasketItems,
              }}
              mechanic={{
                mechanics,
                areMechanicsLoading,
                selectedMechanicId: inputMechanicId,
                onMechanicChange: handleMechanicChange,
              }}
              bulkActions={[
                {action: openItemsSettings, label: i18n.t('entity.addWork.actions.itemsSettings')},
                {action: removeItems, label: i18n.t('general.actions.remove')},
              ]}
              onQuantityChange={handleQuantityChange}
              onEdit={handleEditLabourItem}
              onDelete={handleDeleteLabourItem}
              data-testid={suffixTestId('basket', props)}
            />
          </Box>
        </HStack>
      </Box>
    </FullScreenModal>
  );
}
